// Repeatable product/from-location/qty/price/VAT line rows for the "Dispatch
// without weighbridge" screen (DispatchManual.tsx). Same fields as
// DispatchLinesEditor.tsx (the DRAFT-dispatch power-user editor) but with a
// unit-aware qty placeholder — an EACH product's line is a whole-unit count,
// not a mass (mirrors SalesOrderLinesEditor.tsx's identical `baseUom`-driven
// placeholder, just-landed on the Sales Orders goal) — plus per-row
// `priceTouched` tracking so DispatchManual.tsx can prefill a line's price
// from the customer's contract once a product is picked without clobbering
// anything the user already typed (mirrors DispatchWizard.tsx's own
// `priceTouched` field, kept per-line here instead of once for the whole
// form). A separate component from DispatchLinesEditor rather than extending
// it — that editor is shared by DispatchDetail.tsx's DRAFT editor for an
// already-created dispatch of any origin, and this screen's row shape carries
// `priceTouched` (a prefill concern) that the detail editor has no use for.
import { ActionIcon, Button, Group, Select, Stack, Table, Text, TextInput, Tooltip } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { VAT_TREATMENTS, type VatTreatment } from '@zebra/core';
import { ProductSelect } from './ProductSelect';
import { LocationSelect } from './LocationSelect';
import { qtyInputLabel } from '../format';
import type { ProductRow } from '../types';

export interface DispatchManualLineFormRow {
  key: string;
  productId: string;
  productLabel: string;
  fromLocationId: string;
  locationLabel: string;
  qty: string;
  priceRand: string;
  priceTouched: boolean;
  vatTreatment: VatTreatment;
}

export function emptyDispatchManualLineRow(): DispatchManualLineFormRow {
  return {
    key: crypto.randomUUID(),
    productId: '',
    productLabel: '',
    fromLocationId: '',
    locationLabel: '',
    qty: '',
    priceRand: '',
    priceTouched: false,
    vatTreatment: 'STANDARD',
  };
}

/** True only when every line has a product, a from-location, a positive
 * quantity (a whole number for an EACH product — echoes `positiveQtySchema`/
 * the server's own base_uom semantics client-side; the server remains the
 * actual enforcement point), and a price. Mirrors SalesOrderLinesEditor.tsx's
 * `salesOrderLinesValid`. */
export function dispatchManualLinesValid(rows: DispatchManualLineFormRow[], productsById: Map<string, ProductRow>): boolean {
  if (rows.length === 0) return false;
  return rows.every((row) => {
    if (!row.productId || !row.fromLocationId || !row.priceRand.trim()) return false;
    const qty = Number(row.qty);
    if (!row.qty.trim() || !Number.isFinite(qty) || qty <= 0) return false;
    if (productsById.get(row.productId)?.baseUom === 'EACH' && !Number.isInteger(qty)) return false;
    return true;
  });
}

interface DispatchManualLinesEditorProps {
  rows: DispatchManualLineFormRow[];
  onChange: (rows: DispatchManualLineFormRow[]) => void;
  siteId: string | undefined;
  /** ALL products (active + inactive), keyed by id — resolves each row's
   * baseUom for the qty placeholder, same precedent as
   * SalesOrderLinesEditor.tsx's identical prop. */
  productsById: Map<string, ProductRow>;
}

/** Presentational-only (wire conversion + price-prefill orchestration stay the
 * caller's job, DispatchManual.tsx) — this component only tracks the raw form
 * fields plus the `priceTouched` bit for each row. */
export function DispatchManualLinesEditor({ rows, onChange, siteId, productsById }: DispatchManualLinesEditorProps) {
  function updateRow(key: string, patch: Partial<DispatchManualLineFormRow>) {
    onChange(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }

  function removeRow(key: string) {
    onChange(rows.filter((row) => row.key !== key));
  }

  function addRow() {
    onChange([...rows, emptyDispatchManualLineRow()]);
  }

  return (
    <Stack gap="xs">
      <Table.ScrollContainer minWidth={640}>
      <Table verticalSpacing="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Product</Table.Th>
            <Table.Th>From location</Table.Th>
            {/* Neutral header — a dispatch can mix KG and EACH products, so
                each row's own placeholder ("kg" / "Units") carries the unit
                instead of a single table-wide header that would lie for one
                of them (same precedent as SalesOrderLinesEditor.tsx). */}
            <Table.Th w={120}>
              <Tooltip label="In the product's base unit — kg or count, depending on the product chosen." multiline w={220}>
                <span>Qty</span>
              </Tooltip>
            </Table.Th>
            {/* "ex VAT" spelled out to match the Dispatch wizard's own price
                field ("Price per unit (R, ex VAT)") — USER_GUIDE §6g quotes
                this header verbatim. */}
            <Table.Th w={140}>Price / unit (R, ex VAT)</Table.Th>
            <Table.Th w={140}>VAT</Table.Th>
            <Table.Th w={48} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => {
            const baseUom = productsById.get(row.productId)?.baseUom;
            return (
              <Table.Tr key={row.key}>
                <Table.Td>
                  <ProductSelect
                    value={row.productId}
                    onChange={(productId, productLabel) => updateRow(row.key, { productId, productLabel })}
                    aria-label="Product"
                  />
                </Table.Td>
                <Table.Td>
                  <LocationSelect
                    siteId={siteId}
                    value={row.fromLocationId}
                    onChange={(fromLocationId, locationLabel) => updateRow(row.key, { fromLocationId, locationLabel })}
                    aria-label="From location"
                  />
                </Table.Td>
                <Table.Td>
                  <TextInput
                    aria-label="Quantity"
                    placeholder={qtyInputLabel(baseUom, 'kg', 'Units')}
                    value={row.qty}
                    onChange={(e) => updateRow(row.key, { qty: e.currentTarget.value })}
                  />
                </Table.Td>
                <Table.Td>
                  <TextInput
                    aria-label="Price per unit"
                    placeholder="0.00"
                    value={row.priceRand}
                    onChange={(e) => updateRow(row.key, { priceRand: e.currentTarget.value, priceTouched: true })}
                  />
                </Table.Td>
                <Table.Td>
                  <Select
                    aria-label="VAT treatment"
                    data={VAT_TREATMENTS.map((v) => ({ value: v, label: v }))}
                    value={row.vatTreatment}
                    onChange={(v) => updateRow(row.key, { vatTreatment: (v as VatTreatment) ?? 'STANDARD' })}
                    comboboxProps={{ withinPortal: true }}
                  />
                </Table.Td>
                <Table.Td>
                  <Tooltip label="Remove this line from the dispatch">
                    <ActionIcon color="red" variant="subtle" aria-label="Remove line" onClick={() => removeRow(row.key)} disabled={rows.length <= 1}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
      </Table.ScrollContainer>
      <Group justify="space-between">
        <Button variant="light" leftSection={<IconPlus size={16} />} onClick={addRow}>
          Add line
        </Button>
        {rows.length === 0 && (
          <Text size="sm" c="dimmed">
            At least one line is required.
          </Text>
        )}
      </Group>
    </Stack>
  );
}
